import { useEffect, useMemo, useRef, useState } from 'react'
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FileText,
  Pencil,
  UploadCloud,
  X,
} from 'lucide-react'
import { Button, Chip, Combobox, Drawer, toast } from '@/components/ui'
import { getVendorProducts, revokeLearned, searchVendors } from './api'
import type {
  LearnedMatch,
  LotPlanEntry,
  QueueItem,
  VendorCandidate,
  VendorProduct,
} from './types'
import { useHasAnyPermission } from '@/shell/module-system'
import { useCofaUploadQueue } from './useCofaUploadQueue'

/** Who may apply a CofA with a vendor product conflict (server: COFA_ADMIN_ROLES). */
const CONFLICT_RESOLVER_ROLES = ['admin', 'regulatory'] as const

export interface CofaBatchUploaderProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Called once when the uploader fully closes so the host page can
   * invalidate pipeline / documents queries. Fires even when the queue
   * is empty.
   */
  onClose?: () => void
  /**
   * Called each time a plan finishes applying, so the host page can
   * refresh its lists while the (non-modal) drawer stays open.
   */
  onApplied?: () => void
}

/**
 * Drawer-based batch uploader restored in Sprint 04 (the M11 cutover
 * dropped this surface; see `docs/active/sprint-04-cofa-admin-restoration.md`).
 *
 * Flow:
 *   1. User drops N PDFs on the dropzone → enqueue.
 *   2. The `useCofaUploadQueue` hook processes up to 3 concurrently.
 *   3. Happy path: backend auto-resolves vendor → applies plan → item
 *      enters `applied` state and shows the created/attached lots.
 *   4. `needs_vendor` branch → inline vendor picker within the row.
 *   5. `needs_confirm` branch → inline plan preview with Confirm button.
 *
 * Views stay in one drawer (view-switching was considered, rejected to
 * keep the queue always visible). Principle 4.3 — non-modal drawer so
 * the user can click back to the page while a long queue runs.
 */
export function CofaBatchUploader({
  open,
  onOpenChange,
  onClose,
  onApplied,
}: CofaBatchUploaderProps) {
  const queue = useCofaUploadQueue()
  const {
    items,
    enqueue,
    removeItem,
    retryWithVendor,
    confirmPlan,
    patchLot,
    setPlanVendor,
    cancelConfirm,
    clearTerminal,
    busy,
    needsAttention,
  } = queue

  // Fire onClose once per open->close transition.
  useEffect(() => {
    if (!open) onClose?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Fire onApplied whenever another plan lands in `applied`.
  const appliedCount = items.filter((it) => it.status === 'applied').length
  const prevAppliedCount = useRef(appliedCount)
  useEffect(() => {
    if (appliedCount > prevAppliedCount.current) onApplied?.()
    prevAppliedCount.current = appliedCount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedCount])

  const summary = useMemo(() => {
    const counts = {
      queued: 0,
      processing: 0,
      needs: 0,
      applied: 0,
      error: 0,
    }
    for (const it of items) {
      if (it.status === 'queued') counts.queued++
      else if (it.status === 'processing' || it.status === 'confirming') counts.processing++
      else if (it.status === 'needs_vendor' || it.status === 'needs_confirm') counts.needs++
      else if (it.status === 'applied') counts.applied++
      else if (it.status === 'error') counts.error++
    }
    return counts
  }, [items])

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      size="wide"
      title="Upload CofAs"
      meta={
        items.length > 0 ? (
          <>
            <span>{items.length} total</span>
            {summary.applied > 0 ? <span>· {summary.applied} applied</span> : null}
            {summary.needs > 0 ? (
              <span className="text-warning-text">· {summary.needs} needs action</span>
            ) : null}
            {summary.error > 0 ? (
              <span className="text-danger-text">· {summary.error} errored</span>
            ) : null}
          </>
        ) : (
          <span>Drop vendor CofA PDFs to create or update lots in Odoo</span>
        )
      }
      headerActions={
        items.some((it) => it.status === 'applied' || it.status === 'error') ? (
          <Button variant="ghost" size="sm" onClick={clearTerminal}>
            Clear done
          </Button>
        ) : undefined
      }
      footer={
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-secondary">
            {busy
              ? 'Processing…'
              : needsAttention
                ? 'Resolve the flagged items to continue.'
                : items.length === 0
                  ? 'PDF only. Drop multiple at once — up to 3 process in parallel.'
                  : 'All items handled. Drop more or close when done.'}
          </span>
          <Button variant="primary" size="sm" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3 p-4">
        <Dropzone onFiles={enqueue} />

        {items.length === 0 ? (
          <EmptyHint />
        ) : (
          <ul className="flex flex-col gap-2">
            {items.map((item) => (
              <li key={item.id}>
                <QueueItemRow
                  item={item}
                  onRemove={() => removeItem(item.id)}
                  onPickVendor={(vendorId) =>
                    retryWithVendor(item.id, vendorId, item.file)
                  }
                  onConfirm={() => confirmPlan(item.id, item.plan)}
                  onCancelConfirm={() => cancelConfirm(item.id)}
                  onPatchLot={(lotIdx, patch) =>
                    patchLot(item.id, lotIdx, patch)
                  }
                  onChangeVendor={(vendor) => setPlanVendor(item.id, vendor)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </Drawer>
  )
}

// ─── Dropzone ───────────────────────────────────────────────────────

function Dropzone({ onFiles }: { onFiles: (files: File[]) => void }) {
  const [over, setOver] = useState(false)

  return (
    <label
      onDragEnter={(e) => {
        e.preventDefault()
        setOver(true)
      }}
      onDragOver={(e) => {
        e.preventDefault()
        setOver(true)
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault()
        setOver(false)
        const dropped = Array.from(e.dataTransfer.files)
        const pdfs = dropped.filter(
          (f) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name),
        )
        if (pdfs.length === 0) {
          toast.danger('Only PDF files are accepted.')
          return
        }
        if (pdfs.length < dropped.length) {
          toast.warning(`Skipped ${dropped.length - pdfs.length} non-PDF file(s).`)
        }
        onFiles(pdfs)
      }}
      className={
        'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-sm border-2 border-dashed px-4 py-8 text-sm transition-colors ' +
        (over
          ? 'border-accent bg-accent-bg text-primary'
          : 'border-border-default bg-surface-sunk text-secondary hover:bg-surface-hover')
      }
    >
      <UploadCloud size={24} className="text-tertiary" />
      <span>
        <strong className="text-primary">Drop vendor CofA PDFs</strong> or click to choose
      </span>
      <span className="text-xs text-tertiary">
        Up to 3 process in parallel. PDF only.
      </span>
      <input
        type="file"
        accept="application/pdf"
        multiple
        className="hidden"
        onChange={(e) => {
          const list = Array.from(e.target.files ?? [])
          if (list.length > 0) onFiles(list)
          // Reset so the same file name can be picked again in the session.
          e.currentTarget.value = ''
        }}
      />
    </label>
  )
}

function EmptyHint() {
  return (
    <div className="rounded-sm border border-border-subtle bg-surface-sunk p-3 text-sm text-secondary">
      <p className="mb-1 font-semibold text-primary">What happens when you upload</p>
      <ol className="ml-4 list-decimal space-y-1 text-xs">
        <li>Vendor is auto-detected from the PDF (or you pick it inline).</li>
        <li>Lot numbers + dates are extracted.</li>
        <li>
          If a lot doesn't exist in Odoo yet, it's <strong>created</strong>; if it exists,
          the CofA is attached or updated.
        </li>
        <li>
          SPI-branded products get an SPI CofA queued for review in the pipeline
          (auto-generated by the doc converter).
        </li>
      </ol>
    </div>
  )
}

// ─── Per-item row ───────────────────────────────────────────────────

interface QueueItemRowProps {
  item: QueueItem
  onRemove: () => void
  onPickVendor: (vendorId: number) => void
  onConfirm: () => void
  onCancelConfirm: () => void
  onPatchLot: (lotIdx: number, patch: Partial<LotPlanEntry>) => void
  onChangeVendor: (vendor: { id: number; name: string }) => void
}

function QueueItemRow({
  item,
  onRemove,
  onPickVendor,
  onConfirm,
  onCancelConfirm,
  onPatchLot,
  onChangeVendor,
}: QueueItemRowProps) {
  const [expanded, setExpanded] = useState(
    item.status === 'needs_vendor' || item.status === 'needs_confirm',
  )

  const needsAction =
    item.status === 'needs_vendor' || item.status === 'needs_confirm'

  return (
    <div
      className={
        'rounded-sm border bg-surface ' +
        (needsAction
          ? 'border-warning'
          : item.status === 'error'
            ? 'border-danger'
            : 'border-border-subtle')
      }
    >
      <header className="flex items-center gap-2 px-3 py-2">
        <FileText size={14} className="flex-shrink-0 text-tertiary" />
        <span className="truncate text-sm text-primary" title={item.file.name}>
          {item.file.name}
        </span>
        <StatusChip status={item.status} />
        <div className="ml-auto flex items-center gap-1">
          {needsAction ? (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-label={expanded ? 'Collapse' : 'Expand'}
              className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-sm text-tertiary hover:bg-surface-hover"
            >
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          ) : null}
          <button
            type="button"
            onClick={onRemove}
            aria-label="Remove from queue"
            className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-sm text-tertiary hover:bg-danger-bg hover:text-danger-text"
          >
            <X size={14} />
          </button>
        </div>
      </header>

      {item.message ? (
        <p className="border-t border-border-subtle px-3 py-2 text-xs text-secondary">
          {item.message}
        </p>
      ) : null}

      {expanded && item.status === 'needs_vendor' ? (
        <VendorPickerInline
          candidates={item.vendorCandidates ?? []}
          extractedVendorName={item.extractedVendorName}
          onPick={onPickVendor}
        />
      ) : null}

      {expanded && item.status === 'needs_confirm' && item.plan ? (
        <ConfirmPlanInline
          plan={item.plan}
          vendorLearned={item.vendorLearned}
          onConfirm={onConfirm}
          onCancel={onCancelConfirm}
          onPatchLot={onPatchLot}
          onChangeVendor={onChangeVendor}
        />
      ) : null}

      {item.status === 'applied' && item.appliedLots && item.appliedLots.length > 0 ? (
        <AppliedSummary lots={item.appliedLots} />
      ) : null}
    </div>
  )
}

function StatusChip({ status }: { status: QueueItem['status'] }) {
  switch (status) {
    case 'queued':
      return <Chip size="xs" variant="neutral">queued</Chip>
    case 'processing':
      return <Chip size="xs" variant="info">processing…</Chip>
    case 'needs_vendor':
      return <Chip size="xs" variant="warning">needs vendor</Chip>
    case 'needs_confirm':
      return <Chip size="xs" variant="warning">needs confirm</Chip>
    case 'confirming':
      return <Chip size="xs" variant="info">confirming…</Chip>
    case 'applied':
      return <Chip size="xs" variant="success">applied</Chip>
    case 'error':
      return <Chip size="xs" variant="danger">error</Chip>
  }
}

// ─── Inline vendor picker ───────────────────────────────────────────

function VendorPickerInline({
  candidates,
  extractedVendorName,
  onPick,
}: {
  candidates: VendorCandidate[]
  extractedVendorName?: string
  onPick: (vendorId: number) => void
}) {
  const [q, setQ] = useState('')
  const [searchResults, setSearchResults] = useState<VendorCandidate[] | null>(null)
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState(false)
  const showingSearch = q.trim().length >= 2

  // Debounced vendor search — 200ms after last keystroke. `setSearching`
  // is suppressed because the effect's purpose is the debounced external
  // call + spinner sync.
  useEffect(() => {
    if (!showingSearch) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSearching(true)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSearchError(false)
    const timer = setTimeout(() => {
      searchVendors(q)
        .then((res) => setSearchResults(res))
        .catch(() => {
          // A real backend failure must not read the same as "no vendors
          // matched" — that dead-end (with no distinguishing signal) is
          // exactly what made the D&D upload silently unrecoverable.
          setSearchResults([])
          setSearchError(true)
        })
        .finally(() => setSearching(false))
    }, 200)
    return () => {
      clearTimeout(timer)
    }
  }, [q, showingSearch])

  const shownItems = useMemo(() => {
    // Short-query path: show the candidates the backend already
    // supplied. Search-query path: show server results (or empty while
    // typing into the second character).
    const source = showingSearch ? (searchResults ?? []) : candidates
    return source.map((v) => ({
      value: String(v.id),
      label: v.taught ? `${v.name} — matched before` : v.name,
    }))
  }, [showingSearch, searchResults, candidates])

  return (
    <section className="flex flex-col gap-2 border-t border-border-subtle bg-surface-sunk px-3 py-3">
      <p className="text-xs text-secondary">
        {extractedVendorName ? (
          <>
            PDF says vendor <strong className="text-primary">{extractedVendorName}</strong>.
            {candidates.length > 0 ? ' Closest matches:' : ' No close match — search:'}
          </>
        ) : (
          'Search for the vendor this CofA came from:'
        )}
      </p>
      <Combobox
        items={shownItems}
        placeholder={searching ? 'Searching…' : 'Type ≥2 characters to search vendors'}
        emptyMessage={
          searchError
            ? 'Search failed — try again'
            : q.trim().length < 2 && candidates.length === 0
              ? 'Start typing to search vendors'
              : 'No vendors matched'
        }
        inputValue={q}
        onInputChange={setQ}
        shouldFilter={false}
        onChange={(val) => {
          const id = Number(val)
          if (Number.isFinite(id) && id > 0) onPick(id)
        }}
      />
    </section>
  )
}

// ─── Inline vendor search (confirm-view "Change vendor") ────────────

/**
 * Compact vendor search for the confirm view's "Change vendor" flow.
 * Parallels VendorPickerInline but without the needs_vendor-specific
 * prompt/candidates handling — the user is explicitly swapping rather
 * than resolving an ambiguous extraction.
 */
function VendorSearchInline({
  onPick,
  onCancel,
}: {
  onPick: (vendor: { id: number; name: string }) => void
  onCancel: () => void
}) {
  const [q, setQ] = useState('')
  const [searching, setSearching] = useState(false)
  const [results, setResults] = useState<VendorCandidate[]>([])

  useEffect(() => {
    if (q.trim().length < 2) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- clears search results when query becomes too short to fire a request.
      setResults([])
      return
    }

    setSearching(true)
    const timer = setTimeout(() => {
      searchVendors(q)
        .then((res) => setResults(res))
        .catch(() => setResults([]))
        .finally(() => setSearching(false))
    }, 200)
    return () => clearTimeout(timer)
  }, [q])

  const items = useMemo(
    () => results.map((v) => ({ value: String(v.id), label: v.name })),
    [results],
  )

  const index = useMemo(() => {
    const m = new Map<number, VendorCandidate>()
    for (const v of results) m.set(v.id, v)
    return m
  }, [results])

  return (
    <section className="flex flex-col gap-2 rounded-sm border border-border-default bg-surface px-3 py-2">
      <p className="text-xs text-secondary">
        Search for the correct vendor for this CofA. Changing the vendor clears
        per-lot products; you&apos;ll re-pick them against the new vendor.
      </p>
      <Combobox
        items={items}
        placeholder={searching ? 'Searching…' : 'Type ≥2 characters to search vendors'}
        emptyMessage={
          q.trim().length < 2 ? 'Start typing to search vendors' : 'No vendors matched'
        }
        inputValue={q}
        onInputChange={setQ}
        shouldFilter={false}
        onChange={(val) => {
          const id = Number(val)
          if (!Number.isFinite(id) || id <= 0) return
          const vendor = index.get(id)
          if (vendor) onPick({ id: vendor.id, name: vendor.name })
        }}
      />
      <div className="flex justify-end">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </section>
  )
}

// ─── Inline confirm plan view ───────────────────────────────────────

/** "ryan matched it on Oct 15 (3×)" — why a match came from memory. */
function learnedReason(m: LearnedMatch): string {
  const who = m.last_decided_by ? m.last_decided_by.split('@')[0] : 'someone'
  const when = m.last_decided_at
    ? new Date(m.last_decided_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : null
  const n = m.picks + m.accepts
  return `${who} matched it${when ? ` on ${when}` : ''}${n > 1 ? ` (${n}×)` : ''}`
}

function LearnedBanner({ match, onWrong }: { match: LearnedMatch; onWrong: () => void }) {
  return (
    <div className="flex items-center gap-2 rounded-sm border border-border-default bg-surface px-2 py-1 text-2xs text-secondary">
      <span>
        From the team’s memory: <strong className="text-primary">{match.target_name}</strong> —{' '}
        {learnedReason(match)}.
      </span>
      <button
        type="button"
        onClick={onWrong}
        className="ml-auto shrink-0 cursor-pointer rounded-sm border border-border-default bg-surface px-1.5 py-0.5 text-link hover:bg-surface-hover"
      >
        Wrong?
      </button>
    </div>
  )
}

function ConfirmPlanInline({
  plan,
  vendorLearned,
  onConfirm,
  onCancel,
  onPatchLot,
  onChangeVendor,
}: {
  plan: NonNullable<QueueItem['plan']>
  vendorLearned?: LearnedMatch | null
  onConfirm: () => void
  onCancel: () => void
  onPatchLot: (lotIdx: number, patch: Partial<LotPlanEntry>) => void
  onChangeVendor: (vendor: { id: number; name: string }) => void
}) {
  const [swapOpen, setSwapOpen] = useState(false)
  const [vendorWrong, setVendorWrong] = useState(false)

  // "Wrong?" on a remembered match: tell the server to stop using it (for
  // everyone), then make the person choose. A failed undo is surfaced, not
  // hidden — the match would otherwise come back on the next upload.
  async function markWrong(aliasId: number): Promise<void> {
    try {
      await revokeLearned(aliasId)
      toast.info('Got it — that name won’t auto-match again.')
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      toast.warning(`Couldn’t update the team’s memory: ${msg}`)
    }
  }

  async function markVendorWrong() {
    if (!vendorLearned) return
    await markWrong(vendorLearned.alias_id)
    setVendorWrong(true)
    setSwapOpen(true)
  }

  async function markProductWrong(lotIdx: number) {
    const aliasId = plan.lots[lotIdx]?.learned?.alias_id
    if (aliasId == null) return
    await markWrong(aliasId)
    onPatchLot(lotIdx, {
      product_id: null,
      product_name: '',
      learned: null,
      learned_alias_id: null,
      issues: ['Memory match marked wrong — pick the right product.'],
    })
  }

  // Confirmable recomputes locally from live lot state so teach-and-patch
  // unblocks the Apply button without a round-trip. Matches the legacy
  // CofaConfirmModal rule: every lot must have a product_id and no
  // per-lot issues. Server revalidates on confirm anyway.
  const unresolvedLots = plan.lots.filter(
    (l) =>
      l.product_id == null ||
      (l.issues && l.issues.length > 0) ||
      (l.weak_match_note && !l.weak_match_confirmed),
  )
  const confirmable = plan.lots.length > 0 && unresolvedLots.length === 0

  function handleEditLotName(lotIdx: number, newName: string) {
    const trimmed = newName.trim()
    if (!trimmed) return
    onPatchLot(lotIdx, { lot_name: trimmed })
  }

  // Applies the pick to THIS upload only. Nothing is written to Odoo:
  // vendor product names are never changed by the CofA upload
  // (2026-10-15 — the old "teach" call overwrote FTC's FPD-500N).
  function handlePick(lotIdx: number, product: VendorProduct) {
    if (!plan.lots[lotIdx]) return
    onPatchLot(lotIdx, {
      product_id: product.id,
      product_name: product.name,
      issues: [],
      weak_match_note: null,
      weak_match_confirmed: false,
    })
  }

  return (
    <section className="flex flex-col gap-3 border-t border-border-subtle bg-surface-sunk px-3 py-3">
      <div className="flex items-center gap-2 text-xs text-secondary">
        <strong className="text-primary">{plan.vendor_name}</strong>
        <button
          type="button"
          onClick={() => setSwapOpen((v) => !v)}
          aria-expanded={swapOpen}
          aria-label={swapOpen ? 'Close vendor search' : 'Change vendor'}
          className="cursor-pointer rounded-sm border border-border-default bg-surface px-2 py-0.5 text-2xs text-link hover:bg-surface-hover"
        >
          {swapOpen ? 'Cancel' : 'Change'}
        </button>
        <span>
          {' · '}
          {plan.lots.length} lot{plan.lots.length === 1 ? '' : 's'} extracted
        </span>
      </div>

      {vendorLearned && !vendorWrong ? (
        <LearnedBanner match={vendorLearned} onWrong={() => void markVendorWrong()} />
      ) : null}

      {swapOpen ? (
        <VendorSearchInline
          onPick={(vendor) => {
            onChangeVendor(vendor)
            setSwapOpen(false)
          }}
          onCancel={() => setSwapOpen(false)}
        />
      ) : null}

      {plan.warnings.length > 0 ? (
        <div className="rounded-sm border border-warning bg-warning-bg px-2 py-1.5 text-xs text-warning-text">
          {plan.warnings.map((w, i) => (
            <div key={i}>⚠ {w}</div>
          ))}
        </div>
      ) : null}

      <ul className="flex flex-col gap-1.5">
        {plan.lots.map((lot, i) => (
          <LotPlanCard
            key={`${lot.lot_name}-${i}`}
            lot={lot}
            vendorId={plan.vendor_id}
            onPickProduct={(product) => handlePick(i, product)}
            onConfirmWeak={() => onPatchLot(i, { weak_match_confirmed: true })}
            onEditLotName={(newName) => handleEditLotName(i, newName)}
            onWrongLearned={() => void markProductWrong(i)}
          />
        ))}
      </ul>

      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          disabled={!confirmable}
          onClick={onConfirm}
          title={
            !confirmable
              ? `${unresolvedLots.length} lot(s) still need a product — pick one below`
              : 'Apply this plan'
          }
        >
          {confirmable
            ? 'Apply plan'
            : `${unresolvedLots.length} lot${unresolvedLots.length === 1 ? '' : 's'} unresolved`}
        </Button>
      </div>
    </section>
  )
}

function LotPlanCard({
  lot,
  vendorId,
  onPickProduct,
  onConfirmWeak,
  onEditLotName,
  onWrongLearned,
}: {
  lot: LotPlanEntry
  vendorId: number
  onPickProduct: (product: VendorProduct) => void | Promise<void>
  onConfirmWeak: () => void
  onEditLotName: (newName: string) => void
  onWrongLearned: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(lot.lot_name)
  const [productSwapOpen, setProductSwapOpen] = useState(false)

  // Keep draft in sync if the parent patches lot_name from outside (e.g. vendor change reset)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing draft with external lot_name patch; editing guard prevents clobbering in-progress input
    if (!editing) setDraft(lot.lot_name)
  }, [lot.lot_name, editing])

  function commitEdit() {
    const trimmed = draft.trim()
    if (trimmed) {
      onEditLotName(trimmed)
    } else {
      setDraft(lot.lot_name)
    }
    setEditing(false)
  }

  function startEdit() {
    setDraft(lot.lot_name)
    setEditing(true)
    // Focus happens via autoFocus on the input
  }

  // A vendor CofA conflict that the PO doesn't settle is regulatory's call:
  // no product picker or Change for anyone else (server enforces it too).
  const canResolveConflict = useHasAnyPermission(CONFLICT_RESOLVER_ROLES)
  const lockedForRegulatory = Boolean(lot.regulatory_only) && !canResolveConflict
  const needsProduct =
    lot.product_id == null || (lot.issues != null && lot.issues.length > 0)

  // A guessed product (fuzzy name match) is "verify", not "missing".
  const needsVerify =
    !needsProduct && Boolean(lot.weak_match_note) && !lot.weak_match_confirmed

  const statusTone =
    lot.status === 'blocked' || needsProduct
      ? 'danger'
      : needsVerify
        ? 'warning'
        : lot.status === 'new'
        ? 'info'
        : lot.status === 'exists_update'
          ? 'warning'
          : 'neutral'

  const statusLabel =
    needsProduct
      ? 'pick product'
      : needsVerify
        ? 'verify product'
        : lot.status === 'new'
        ? 'will create'
        : lot.status === 'exists_attach'
          ? 'attach CofA'
          : lot.status === 'exists_update'
            ? 'update fields'
            : lot.status === 'blocked'
              ? 'blocked'
              : lot.status

  return (
    <li className="flex flex-col gap-1 rounded-sm border border-border-subtle bg-surface px-2.5 py-1.5">
      <header className="group flex items-center gap-2">
        {editing ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitEdit() }
              if (e.key === 'Escape') { setDraft(lot.lot_name); setEditing(false) }
            }}
            className="min-w-0 w-24 border-b border-border-default bg-transparent text-xs font-semibold text-primary outline-none"
            aria-label="Edit lot number"
          />
        ) : (
          <button
            type="button"
            onClick={startEdit}
            title="Edit lot number"
            className="flex min-w-0 items-center gap-1 text-xs font-semibold text-primary"
          >
            <span className="truncate">{lot.lot_name}</span>
            <Pencil size={10} className="shrink-0 text-tertiary opacity-0 group-hover:opacity-100" />
          </button>
        )}
        <Chip size="xs" variant={statusTone}>{statusLabel}</Chip>
        <span className="ml-auto truncate text-xs text-secondary">
          {lot.product_name || '— unknown product'}
        </span>
        {!needsProduct && !lot.conflict ? (
          <button
            type="button"
            onClick={() => setProductSwapOpen((v) => !v)}
            aria-expanded={productSwapOpen}
            aria-label={productSwapOpen ? 'Close product search' : 'Change product'}
            className="shrink-0 cursor-pointer rounded-sm border border-border-default bg-surface px-1.5 py-0.5 text-2xs text-link hover:bg-surface-hover"
          >
            {productSwapOpen ? 'Cancel' : 'Change'}
          </button>
        ) : null}
      </header>
      {lot.mfg_date || lot.exp_date ? (
        <div className="text-role-metadata flex gap-3">
          {lot.mfg_date ? <span>Mfg: {lot.mfg_date}</span> : null}
          {lot.exp_date ? <span>Exp: {lot.exp_date}</span> : null}
        </div>
      ) : null}
      {lot.issues && lot.issues.length > 0 ? (
        <div className="text-xs text-danger-text">
          {lot.issues.map((iss, i) => (
            <div key={i}>× {iss}</div>
          ))}
        </div>
      ) : null}
      {needsVerify ? (
        <div className="flex items-center gap-2 rounded-sm border border-warning bg-warning-bg px-2 py-1.5 text-xs text-warning-text">
          <span className="min-w-0 flex-1">{lot.weak_match_note}</span>
          <button
            type="button"
            onClick={onConfirmWeak}
            className="shrink-0 cursor-pointer rounded-sm border border-border-default bg-surface px-2 py-0.5 text-2xs text-link hover:bg-surface-hover"
          >
            Looks right
          </button>
        </div>
      ) : null}
      {lot.learned && !lot.conflict ? (
        <LearnedBanner match={lot.learned} onWrong={onWrongLearned} />
      ) : null}
      {lot.conflict ? (
        <div role="alert" className="rounded-sm border border-warning-border bg-warning-bg px-2 py-1 text-xs text-warning-text">
          <strong>Sent to regulatory.</strong> {lot.conflict}{' '}
          {lockedForRegulatory
            ? 'Regulatory must resolve this CofA; it can’t be applied from your account.'
            : 'The SPI CofA and shipping stay blocked until regulatory verifies the vendor CofA in CofA Management.'}
        </div>
      ) : null}
      {(needsProduct || productSwapOpen) && !lockedForRegulatory ? (
        <ProductPickerInline
          vendorId={vendorId}
          extractedHint={lot.extracted_product_identifier ?? ''}
          manualOverride={!needsProduct && productSwapOpen}
          onPick={(product) => {
            setProductSwapOpen(false)
            return onPickProduct(product)
          }}
        />
      ) : null}
    </li>
  )
}

// ─── Inline product picker (auto-resolve miss, or user-triggered "Change") ─

function ProductPickerInline({
  vendorId,
  extractedHint,
  manualOverride = false,
  onPick,
}: {
  vendorId: number
  extractedHint: string
  /** True when opened via the "Change" toggle on an already-resolved lot —
   *  the extraction actually matched, so the copy/styling shouldn't imply
   *  a miss (unlike the auto-resolve-failed case this component also renders). */
  manualOverride?: boolean
  onPick: (product: VendorProduct) => void | Promise<void>
}) {
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(false)
  const [vendorProducts, setVendorProducts] = useState<VendorProduct[]>([])
  const [otherProducts, setOtherProducts] = useState<VendorProduct[]>([])
  const [picking, setPicking] = useState(false)

  // Load the vendor's supplierinfo-linked catalog on mount (no query yet)
  // so the picker opens with something useful. Searches extend via `q`.
  useEffect(() => {
    let cancelled = false
    // eslint-disable-next-line react-hooks/set-state-in-effect -- standard fetch-on-mount pattern; cancelled flag prevents stale writes.
    setLoading(true)
    // The CofA's printed text lets the server list already-matched products
    // first; without it the call is exactly as before.
    ;(extractedHint ? getVendorProducts(vendorId, '', extractedHint) : getVendorProducts(vendorId, ''))
      .then((resp) => {
        if (cancelled) return
        setVendorProducts(resp.vendor_products)
        setOtherProducts(resp.other_products)
      })
      .catch(() => {
        if (cancelled) return
        setVendorProducts([])
        setOtherProducts([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // vendorId is per-lot-parent and stable; re-running only on change.
  }, [vendorId, extractedHint])

  // Debounced search-as-you-type: 200ms after last keystroke. Only fires
  // at ≥2 chars to avoid hammering the catalog with single-letter queries.
  useEffect(() => {
    if (q.trim().length < 2) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true)
    const t = setTimeout(() => {
      ;(extractedHint ? getVendorProducts(vendorId, q, extractedHint) : getVendorProducts(vendorId, q))
        .then((resp) => {
          setVendorProducts(resp.vendor_products)
          setOtherProducts(resp.other_products)
        })
        .catch(() => {
          setVendorProducts([])
          setOtherProducts([])
        })
        .finally(() => setLoading(false))
    }, 200)
    return () => clearTimeout(t)
  }, [q, vendorId, extractedHint])

  const items = useMemo(() => {
    // Vendor-linked products first under one group header, then catalog-
    // expansion matches under a second header. Combobox renders groups
    // in first-seen order, so order here determines visual order.
    // The vendor's own name next to ours — the CofA prints theirs.
    const vp = vendorProducts.map((p) => {
      const name = p.vendor_name ? `${p.name} · ${p.vendor_name}` : p.name
      return {
        value: String(p.id),
        label: p.taught ? `${name} — matched before` : name,
        meta: p.open_po ? `open PO ${p.open_po}` : p.default_code || undefined,
        group: 'Linked to this vendor',
      }
    })
    const op = otherProducts.map((p) => ({
      value: String(p.id),
      label: p.taught ? `${p.name} — matched before` : p.name,
      meta: p.default_code || undefined,
      group: 'Elsewhere in catalog',
    }))
    return [...vp, ...op]
  }, [vendorProducts, otherProducts])

  const index = useMemo(() => {
    const m = new Map<number, VendorProduct>()
    for (const p of vendorProducts) m.set(p.id, p)
    for (const p of otherProducts) m.set(p.id, p)
    return m
  }, [vendorProducts, otherProducts])

  return (
    <div
      className={
        'mt-1 flex flex-col gap-1.5 rounded-sm border px-2 py-2 ' +
        (manualOverride
          ? 'border-border-default bg-surface-sunk'
          : 'border-warning bg-warning-bg')
      }
    >
      <p className={'text-xs ' + (manualOverride ? 'text-secondary' : 'text-warning-text')}>
        {manualOverride ? (
          <>Pick a different SPI product for this lot.</>
        ) : extractedHint ? (
          <>
            PDF product: <strong>{extractedHint}</strong> — not found. Pick the
            SPI product this CofA is for. Your pick is remembered for this
            vendor’s exact product name, and matches automatically after a
            second pick (or when the open PO agrees).
          </>
        ) : (
          <>Pick the SPI product this CofA is for.</>
        )}
      </p>
      <Combobox
        items={items}
        placeholder={
          loading
            ? 'Loading vendor products…'
            : picking
              ? 'Applying…'
              : 'Search this vendor’s products or the full catalog'
        }
        emptyMessage={
          q.trim().length < 2 && vendorProducts.length === 0 && !loading
            ? "This vendor has no mapped products yet — type ≥2 characters to search the full catalog"
            : 'No matching products'
        }
        inputValue={q}
        onInputChange={setQ}
        shouldFilter={false}
        onChange={(val) => {
          const id = Number(val)
          if (!Number.isFinite(id) || id <= 0) return
          const product = index.get(id)
          if (!product) return
          setPicking(true)
          void Promise.resolve(onPick(product)).finally(() => setPicking(false))
        }}
      />
    </div>
  )
}


// ─── Applied summary ────────────────────────────────────────────────

function AppliedSummary({
  lots,
}: {
  lots: NonNullable<QueueItem['appliedLots']>
}) {
  return (
    <section className="flex flex-col gap-1 border-t border-border-subtle bg-success-bg px-3 py-2 text-xs">
      <div className="flex items-center gap-1.5 text-success-text">
        <CheckCircle2 size={12} />
        <strong>Applied to {lots.length} lot{lots.length === 1 ? '' : 's'}</strong>
      </div>
      <ul className="pl-5">
        {lots.map((lot, i) => (
          <li key={`${lot.lot_id}-${i}`} className="flex gap-2 text-2xs text-secondary">
            <span className="text-primary">{lot.lot_number}</span>
            {lot.product_name ? <span>· {lot.product_name}</span> : null}
            <span>· {lot.status}</span>
            {lot.spi_cofa_pending ? (
              <span className="text-info-text">· SPI CofA queued</span>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  )
}

